import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { C, S } from "../customers/theme";
import { useAuth } from "../auth/AuthProvider";
import { getBusinessSettings, saveBusinessSettings, PAYMENT_TERMS, CURRENCIES, VAT_RATES } from "../lib/businessSettings";
import { resizeImageFile } from "./imageUtils";
import DemoDataPanel from "./DemoDataPanel";

// Blank counts as null/undefined/whitespace-only — a name that's just spaces
// is not a saved value, and must not block the signup-name prefill below.
const isBlank = (v) => v == null || String(v).trim() === "";

let _seq = 0;
function Field({ label, value, onChange, type = "text", options, placeholder, area }) {
  const id = useMemo(() => `bs-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      {options ? (
        <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : area ? (
        <textarea id={id} style={{ ...S.input, height: 60 }} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input id={id} style={S.input} type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div style={{ ...S.card, marginBottom: 16 }}>
    <div style={{ ...S.sectionTitle, marginTop: 0 }}>{title}</div>
    <div style={S.grid2}>{children}</div>
  </div>
);

const BLANK = {
  legal_name: "", trading_name: "", registration_number: "", vat_number: "",
  email: "", phone: "", website: "",
  address_line1: "", address_line2: "", city: "", county: "", postal_code: "", country: "Ireland",
  logo_url: "",
  bank_account_name: "", bank_name: "", iban: "", bic: "", sort_code: "", account_number: "",
  default_payment_terms: "net_30", default_currency: "EUR", default_vat_rate: 23, default_terms_text: "", invoice_footer: "",
};

export default function BusinessSettings() {
  const { companyName, refreshMembership } = useAuth();
  const [f, setF] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  // True when there's nothing but the signup name to show yet — no settings
  // row at all, OR a row exists with both legal_name and trading_name blank.
  // Drives both the first-run banner and the one-time prefill below; never
  // true once either name has been saved.
  const [isNew, setIsNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  // companyName legitimately changes after mount — AuthProvider resolves it
  // in two steps (companies.name immediately, then the deferred
  // getBusinessName() refinement a beat later), and again on every
  // subsequent token refresh. `load` must NOT depend on it directly: with
  // companyName in its dependency array, every one of those changes
  // re-triggers the mount effect below and re-runs setF({ ...BLANK, ...row }),
  // silently discarding whatever the user is mid-typing. A ref mirrors the
  // latest value for load() to read without making load() itself unstable.
  const companyNameRef = useRef(companyName);
  useEffect(() => { companyNameRef.current = companyName; }, [companyName]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const row = await getBusinessSettings();
      const bothNamesBlank = !row || (isBlank(row.legal_name) && isBlank(row.trading_name));
      // Prefill ONLY when both names are genuinely blank — this never
      // overwrites a value the user has saved, because that branch can't be
      // reached once either name is non-blank.
      const prefillLegalName = bothNamesBlank && !isBlank(companyNameRef.current) ? companyNameRef.current : undefined;
      if (row) setF({ ...BLANK, ...row, ...(prefillLegalName ? { legal_name: prefillLegalName } : {}) });
      else setF(prefillLegalName ? { ...BLANK, legal_name: prefillLegalName } : BLANK);
      setIsNew(bothNamesBlank);
    } catch (e) { setError(e.message || "Could not load business settings."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); setSaved(false); };

  const onLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setError("");
    try { const dataUri = await resizeImageFile(file); set("logo_url")(dataUri); }
    catch (err) { setError(err.message); }
  };

  const save = async () => {
    setBusy(true); setError(""); setSaved(false);
    try {
      await saveBusinessSettings(f);
      setIsNew(false); setSaved(true);
      // Only on a SUCCESSFUL save — a failed save must not refresh the
      // context, or a save that didn't work would look like it partly did.
      await refreshMembership();
      await load();
    } catch (e) { setError(e.message || "Could not save business settings."); }
    finally { setBusy(false); }
  };

  if (loading) return <div style={S.empty}>Loading…</div>;

  return (
    <div style={{ maxWidth: 900 }}>
      {isNew && (
        <div style={{ background: C.surface2, border: `1px solid ${C.accent || C.border}`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: C.textMuted }}>
          👋 <strong style={{ color: C.text }}>Set up your business profile.</strong> This is entered once and reused across your invoices and documents — you'll never retype it per document. The legal name below was taken from your signup and can be corrected here.
        </div>
      )}

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={() => setError("")}>Dismiss</button>
        </div>
      )}

      <Section title="Business identity">
        <Field label="Legal name" value={f.legal_name} onChange={set("legal_name")} placeholder="Your registered company name" />
        <Field label="Trading name" value={f.trading_name} onChange={set("trading_name")} />
        <Field label="Company reg. number" value={f.registration_number} onChange={set("registration_number")} placeholder="CRO number" />
        <Field label="VAT number" value={f.vat_number} onChange={set("vat_number")} placeholder="IE1234567T" />
      </Section>

      <Section title="Contact">
        <Field label="Email" type="email" value={f.email} onChange={set("email")} />
        <Field label="Phone" value={f.phone} onChange={set("phone")} />
        <Field label="Website" value={f.website} onChange={set("website")} placeholder="https://…" />
      </Section>

      <Section title="Address">
        <Field label="Address line 1" value={f.address_line1} onChange={set("address_line1")} />
        <Field label="Address line 2" value={f.address_line2} onChange={set("address_line2")} />
        <Field label="City / town" value={f.city} onChange={set("city")} />
        <Field label="County" value={f.county} onChange={set("county")} />
        <Field label="Postal code" value={f.postal_code} onChange={set("postal_code")} />
        <Field label="Country" value={f.country} onChange={set("country")} />
      </Section>

      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ ...S.sectionTitle, marginTop: 0 }}>Logo</div>
        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          <div style={{ width: 96, height: 96, borderRadius: 8, border: `1px dashed ${C.border}`, background: C.surface2, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
            {f.logo_url ? <img src={f.logo_url} alt="Business logo" style={{ maxWidth: "100%", maxHeight: "100%" }} /> : <span style={{ color: C.textDim, fontSize: 12 }}>No logo</span>}
          </div>
          <div>
            <label style={{ ...S.btn("secondary"), display: "inline-block", cursor: "pointer" }}>
              Upload logo<input type="file" accept="image/*" onChange={onLogo} style={{ display: "none" }} aria-label="Upload logo" />
            </label>
            {f.logo_url && <button style={{ ...S.btn("ghost"), marginLeft: 8 }} onClick={() => set("logo_url")("")}>Remove</button>}
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 6 }}>Image only (PNG/JPG/SVG). Resized automatically; ~200 KB max.</div>
          </div>
        </div>
      </div>

      <Section title="Bank details (shown on invoices for payment)">
        <Field label="Account name" value={f.bank_account_name} onChange={set("bank_account_name")} />
        <Field label="Bank name" value={f.bank_name} onChange={set("bank_name")} />
        <Field label="IBAN" value={f.iban} onChange={set("iban")} />
        <Field label="BIC / SWIFT" value={f.bic} onChange={set("bic")} />
        <Field label="Sort code" value={f.sort_code} onChange={set("sort_code")} />
        <Field label="Account number" value={f.account_number} onChange={set("account_number")} />
      </Section>

      <Section title="Document defaults">
        <Field label="Default payment terms" value={f.default_payment_terms} onChange={set("default_payment_terms")} options={PAYMENT_TERMS} />
        <Field label="Default currency" value={f.default_currency} onChange={set("default_currency")} options={CURRENCIES} />
        <Field label="Default VAT rate (%)" value={f.default_vat_rate} onChange={(v) => set("default_vat_rate")(v)} options={VAT_RATES.map((r) => ({ value: r, label: `${r}%` }))} />
        <div />
        <div style={{ gridColumn: "span 2" }}><Field label="Default terms / notes" area value={f.default_terms_text} onChange={set("default_terms_text")} placeholder="Payment due within 30 days…" /></div>
        <div style={{ gridColumn: "span 2" }}><Field label="Invoice footer" area value={f.invoice_footer} onChange={set("invoice_footer")} placeholder="Thank you for your business." /></div>
      </Section>

      <DemoDataPanel />

      <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "flex-end", position: "sticky", bottom: 0, background: C.bg, padding: "12px 0" }}>
        {saved && <span style={{ color: C.success, fontSize: 13, fontWeight: 600 }}>✓ Saved</span>}
        <button style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={save} disabled={busy}>{busy ? "Saving…" : "Save business settings"}</button>
      </div>
    </div>
  );
}
